import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type LogRow = {
  id: number;
  template: string;
  recipient: string;
  payload: Record<string, unknown>;
  status: 'queued' | 'sent' | 'failed' | 'retried';
  http_status: number | null;
  message_id: string | null;
  error_detail: string | null;
  retried_from_id: number | null;
  retried_at: string | null;
  created_at: string;
  resolved_at: string | null;
};

type StatRow = { template: string; total: number; sent: number; failed: number; queued: number; retried: number };

type StatusFilter = 'all' | LogRow['status'];

const statusTone: Record<LogRow['status'], { fg: string; bg: string; label: string }> = {
  sent:    { fg: '#1b5d2c', bg: '#dfe9dc', label: 'Sent' },
  queued:  { fg: '#7a5a16', bg: '#f6e8d4', label: 'Queued' },
  failed:  { fg: '#7c1f1f', bg: '#f5dcd9', label: 'Failed' },
  retried: { fg: '#5d5852', bg: '#ece6da', label: 'Retried' },
};

export default function EmailLog() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [stats, setStats] = useState<StatRow[]>([]);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [windowDays, setWindowDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    let query = supabase
      .from('ga_email_log')
      .select('*')
      .order('id', { ascending: false })
      .limit(200);
    if (filter !== 'all') query = query.eq('status', filter);
    const [{ data: r }, { data: s }] = await Promise.all([
      query,
      supabase.rpc('ga_email_stats', { p_days: windowDays }),
    ]);
    setRows((r ?? []) as LogRow[]);
    setStats((s ?? []) as StatRow[]);
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter, windowDays]);

  // Live updates: re-poll every 15s so admins watching the page see new
  // sends without having to refresh.
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filter, windowDays]);

  async function retry(id: number) {
    setRetrying(id);
    const { error } = await supabase.rpc('ga_email_retry', { p_log_id: id });
    setRetrying(null);
    if (error) {
      setToast(`Retry failed: ${error.message}`);
    } else {
      setToast(`Retried · check the top of the table for the new send`);
      await load();
    }
    setTimeout(() => setToast(null), 3500);
  }

  // ─── Aggregate counts across all templates ────────────────────────────
  const totals = useMemo(() => stats.reduce(
    (acc, s) => ({
      total:   acc.total   + Number(s.total),
      sent:    acc.sent    + Number(s.sent),
      failed:  acc.failed  + Number(s.failed),
      queued:  acc.queued  + Number(s.queued),
      retried: acc.retried + Number(s.retried),
    }),
    { total: 0, sent: 0, failed: 0, queued: 0, retried: 0 },
  ), [stats]);

  const successRate = totals.total === 0
    ? 100
    : Math.round((totals.sent / Math.max(1, totals.total - totals.queued)) * 100);

  return (
    <div>
      {/* ─── Heading + window selector ─────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Activity</div>
          <h1 className="display" style={{ fontSize: 30, margin: 0 }}>Email log</h1>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[1, 7, 30].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className="btn btn-sm"
              style={{
                background: windowDays === d ? 'var(--ink)' : 'transparent',
                color: windowDays === d ? 'var(--bg)' : 'var(--ink)',
                border: '1.5px solid var(--ink)',
              }}
            >{d === 1 ? '24h' : `${d}d`}</button>
          ))}
          <Link to="/dashboard/emails/templates" className="btn btn-sm btn-ghost">Templates →</Link>
        </div>
      </div>

      {/* ─── Stat tiles ───────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <Stat label="Total" value={totals.total} sub={`Last ${windowDays === 1 ? '24h' : `${windowDays}d`}`} />
        <Stat label="Sent" value={totals.sent} sub={`${successRate}% success`} tone="ok" />
        <Stat label="Failed" value={totals.failed} sub={totals.failed > 0 ? 'Needs retry' : 'All clean'} tone={totals.failed > 0 ? 'bad' : undefined} />
        <Stat label="Queued" value={totals.queued} sub={totals.queued > 0 ? 'Resolving…' : 'Idle'} tone={totals.queued > 0 ? 'warn' : undefined} />
      </div>

      {/* ─── Per-template breakdown ──────────────────────────────── */}
      {stats.length > 0 && (
        <details style={{ marginBottom: 18 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--ink-soft)', userSelect: 'none', marginBottom: 8 }}>
            By template
          </summary>
          <div style={{ background: 'var(--card)', borderRadius: 12, padding: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Template</th>
                  <th style={thRight}>Total</th>
                  <th style={thRight}>Sent</th>
                  <th style={thRight}>Failed</th>
                  <th style={thRight}>Queued</th>
                  <th style={thRight}>Retried</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.template}>
                    <td style={td}><code style={codeStyle}>{s.template}</code></td>
                    <td style={tdRight}>{s.total}</td>
                    <td style={{ ...tdRight, color: 'var(--success)' }}>{s.sent}</td>
                    <td style={{ ...tdRight, color: s.failed > 0 ? 'var(--error)' : 'var(--ink-soft)' }}>{s.failed}</td>
                    <td style={tdRight}>{s.queued}</td>
                    <td style={tdRight}>{s.retried}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {/* ─── Filter pills ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {(['all', 'sent', 'failed', 'queued', 'retried'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="btn btn-sm"
            style={{
              background: filter === f ? 'var(--ink)' : 'transparent',
              color: filter === f ? 'var(--bg)' : 'var(--ink)',
              border: '1.5px solid ' + (filter === f ? 'var(--ink)' : 'var(--line)'),
              textTransform: 'capitalize',
            }}
          >{f}</button>
        ))}
      </div>

      {/* ─── Log table ────────────────────────────────────────────── */}
      <div style={{ background: 'var(--card)', borderRadius: 14, overflow: 'hidden' }}>
        {loading && rows.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-soft)', fontSize: 13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-soft)', fontSize: 13 }}>
            No emails in this window yet. {filter !== 'all' && <button onClick={() => setFilter('all')} style={linkBtn}>Clear filter</button>}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead style={{ background: 'rgba(0,0,0,0.02)' }}>
              <tr>
                <th style={th}>When</th>
                <th style={th}>Template</th>
                <th style={th}>To</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const tone = statusTone[row.status];
                return (
                  <Fragment key={row.id}>
                    <tr style={{ borderTop: '1px solid var(--line)' }}>
                      <td style={td}>
                        <span title={new Date(row.created_at).toLocaleString()}>{relativeTime(row.created_at)}</span>
                      </td>
                      <td style={td}><code style={codeStyle}>{row.template}</code></td>
                      <td style={{ ...td, fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: 12, color: 'var(--ink)' }}>{row.recipient}</td>
                      <td style={td}>
                        <span style={{ background: tone.bg, color: tone.fg, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 999 }}>
                          {tone.label}
                          {row.http_status && row.status === 'failed' ? ` · ${row.http_status}` : ''}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button onClick={() => setExpanded(expanded === row.id ? null : row.id)} style={linkBtn}>
                            {expanded === row.id ? 'Hide' : 'Details'}
                          </button>
                          {row.status === 'failed' && (
                            <button
                              onClick={() => retry(row.id)}
                              className="btn btn-sm"
                              style={{ background: 'var(--primary)', color: 'var(--ink)', border: '1.5px solid var(--primary)', padding: '6px 12px' }}
                              disabled={retrying === row.id}
                            >
                              {retrying === row.id ? '…' : 'Retry'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded === row.id && (
                      <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
                        <td colSpan={5} style={{ padding: '14px 16px' }}>
                          <Detail row={row} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: 'var(--ink)', color: 'var(--bg)', padding: '12px 18px', borderRadius: 999, fontSize: 13, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
          {toast}
        </div>
      )}
    </div>
  );
}

// React import shim — we use Fragment from 'react' without importing it.
import { Fragment } from 'react';

// ─── Helpers ──────────────────────────────────────────────────────────────

function Stat({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: 'ok' | 'bad' | 'warn' }) {
  const accent = tone === 'ok' ? 'var(--success)' : tone === 'bad' ? 'var(--error)' : tone === 'warn' ? 'var(--primary)' : 'var(--ink)';
  return (
    <div style={{ background: 'var(--card)', borderRadius: 14, padding: 18 }}>
      <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 6 }}>{label}</div>
      <div className="display" style={{ fontSize: 32, lineHeight: 1, color: accent }}>{value.toLocaleString()}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Detail({ row }: { row: LogRow }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '8px 16px', fontSize: 13 }}>
      {row.message_id && <><Key>Resend ID</Key><Val><code style={codeStyle}>{row.message_id}</code></Val></>}
      {row.http_status !== null && <><Key>HTTP</Key><Val>{row.http_status}</Val></>}
      {row.resolved_at && <><Key>Resolved</Key><Val title={new Date(row.resolved_at).toLocaleString()}>{relativeTime(row.resolved_at)}</Val></>}
      {row.retried_from_id && <><Key>Retried from</Key><Val>#{row.retried_from_id}</Val></>}
      {row.retried_at && <><Key>Retried at</Key><Val>{relativeTime(row.retried_at)}</Val></>}
      {row.error_detail && (
        <>
          <Key>Error</Key>
          <Val><pre style={{ margin: 0, fontSize: 12, color: 'var(--error)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.error_detail}</pre></Val>
        </>
      )}
      <Key>Payload</Key>
      <Val>
        <pre style={{ margin: 0, fontSize: 11, background: 'var(--bg)', padding: 12, borderRadius: 8, overflow: 'auto', maxHeight: 280 }}>
{JSON.stringify(row.payload, null, 2)}
        </pre>
      </Val>
    </div>
  );
}

const Key = ({ children }: { children: React.ReactNode }) => (
  <div className="mono" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', paddingTop: 4 }}>{children}</div>
);
const Val = ({ children, title }: { children: React.ReactNode; title?: string }) => (
  <div title={title} style={{ color: 'var(--ink)' }}>{children}</div>
);

const th: React.CSSProperties = { textAlign: 'left', padding: '12px 16px', fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-soft)', fontWeight: 600 };
const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '12px 16px', verticalAlign: 'middle', color: 'var(--ink)' };
const tdRight: React.CSSProperties = { ...td, textAlign: 'right' };
const codeStyle: React.CSSProperties = { background: 'var(--bg)', borderRadius: 4, padding: '2px 6px', fontSize: 12 };
const linkBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--ink-soft)', textDecoration: 'underline', fontSize: 12, cursor: 'pointer', padding: '6px 8px' };

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
